/**
 * Små delar som både "Nytt möte", listan och mötesvyn använder.
 * Bara komponenter i den här filen (Fast Refresh).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { Kalla, MotesTyp, Status } from '../../../../netlify/functions/_shared/moten/typer';
import { INBJUDNINGSRAD, TYP_ETIKETT, TYP_FORKLARING, statusKort } from './motesText';

/** Menyglyf för Möten: en ljudvåg. Egen glyf i stället för en ikon ur biblioteket. */
export function MotesGlyf({
  size = 16,
  strokeWidth = 2,
  className,
}: {
  size?: number | string;
  strokeWidth?: number | string;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      className={className}
      aria-hidden
    >
      <path d="M4 10v4M8 6v12M12 3v18M16 7v10M20 10v4" />
    </svg>
  );
}

/** Versal etikett ovanför ett fält – samma form som på Feedback-sidan. */
export function Etikett({ htmlFor, children }: { htmlFor?: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="block text-[11px] font-bold uppercase tracking-[0.12em] text-ink-500 mb-2">
      {children}
    </label>
  );
}

/**
 * Status som piller. Färg bara där den betyder något: fel är rött, pågående
 * har en prick, allt annat är neutralt.
 */
export function StatusPiller({
  status,
  kalla,
  fel: notis = null,
  className,
}: {
  status: Status;
  kalla: Kalla;
  /** Notisen på ett möte som inte är i status fel (t.ex. utskrift inte påslagen). */
  fel?: string | null;
  className?: string;
}) {
  const fel = status === 'fel';
  const pagar = !fel && status !== 'sammanfattad' && status !== 'bokad';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-semibold whitespace-nowrap',
        fel ? 'bg-red-50 text-red-700' : 'bg-ink-100 text-ink-700',
        className,
      )}
    >
      {pagar && <span className="w-1.5 h-1.5 rounded-full bg-ink-500 shrink-0" aria-hidden />}
      {fel && <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" aria-hidden />}
      {statusKort(status, kalla, notis)}
    </span>
  );
}

const TYPER: MotesTyp[] = ['intern_alla', 'intern_begransad', 'kund'];

/** Typval med riktiga radioknappar (piltangenter och en tabbstopp gratis). */
export function TypValjare({
  value,
  onChange,
  disabled,
  namn,
}: {
  value: MotesTyp;
  onChange: (t: MotesTyp) => void;
  disabled?: boolean;
  namn: string;
}) {
  return (
    <fieldset className="border-0 p-0 m-0 min-w-0">
      <legend className="block text-[11px] font-bold uppercase tracking-[0.12em] text-ink-500 mb-2 p-0">
        Vem får läsa mötet?
      </legend>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {TYPER.map((t) => {
          const aktiv = value === t;
          return (
            <label key={t} className="relative cursor-pointer min-w-0">
              <input
                type="radio"
                name={namn}
                value={t}
                checked={aktiv}
                onChange={() => onChange(t)}
                disabled={disabled}
                className="sr-only peer"
              />
              <span
                className={cn(
                  'block h-full rounded-lg border px-3.5 py-3 transition-colors',
                  'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2',
                  aktiv
                    ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500'
                    : 'border-ink-200 bg-surface hover:border-ink-300',
                )}
              >
                <span className={cn('block text-[13.5px] font-semibold', aktiv ? 'text-brand-900' : 'text-ink-900')}>
                  {TYP_ETIKETT[t]}
                </span>
                <span className="block text-[12px] text-ink-500 mt-0.5 leading-snug">{TYP_FORKLARING[t]}</span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/** Kundmötets färdiga rad till inbjudan, med kopieringsknapp. */
export function Inbjudningsrad() {
  const [kopierad, setKopierad] = useState(false);
  const [kunde, setKunde] = useState(true);

  useEffect(() => {
    if (!kopierad) return;
    const t = setTimeout(() => setKopierad(false), 2500);
    return () => clearTimeout(t);
  }, [kopierad]);

  async function kopiera() {
    try {
      await navigator.clipboard.writeText(INBJUDNINGSRAD);
      setKunde(true);
      setKopierad(true);
    } catch {
      setKunde(false);
    }
  }

  return (
    <div className="rounded-lg border border-ink-200 bg-surface-sunken p-3.5">
      <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-500">Klistra in i kundens inbjudan</p>
      <p className="mt-1.5 text-[13.5px] text-ink-900 leading-relaxed select-all">{INBJUDNINGSRAD}</p>
      <div className="mt-2.5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={kopiera}
          className="h-8 px-3 rounded-lg border border-ink-200 bg-surface text-[12.5px] font-semibold text-ink-700 hover:bg-ink-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
        >
          Kopiera raden
        </button>
        <span role="status" className="text-[12px] text-ink-500">
          {kopierad ? 'Kopierad.' : !kunde ? 'Kunde inte kopiera – markera raden och kopiera den själv.' : ''}
        </span>
      </div>
    </div>
  );
}

/** Kort med rubrik, samma form som sidans övriga kort. */
export function Sektion({
  rubrik,
  children,
  hoger,
  className,
  id,
}: {
  rubrik: string;
  children: ReactNode;
  hoger?: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section
      aria-labelledby={id}
      className={cn('rounded-xl border border-ink-200 bg-surface shadow-card p-5', className)}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 id={id} className="text-[14px] font-bold text-ink-900">
          {rubrik}
        </h3>
        {hoger}
      </div>
      {children}
    </section>
  );
}

/** Lugn rad för fel eller besked. */
export function Besked({ ton, children }: { ton: 'fel' | 'info'; children: ReactNode }) {
  return ton === 'fel' ? (
    <div
      role="alert"
      className="text-[12.5px] text-red-800 bg-red-50 border border-red-100 rounded-lg px-3.5 py-2.5 leading-relaxed"
    >
      {children}
    </div>
  ) : (
    <div
      role="status"
      className="text-[12.5px] text-ink-700 bg-ink-50 border border-ink-200 rounded-lg px-3.5 py-2.5 leading-relaxed"
    >
      {children}
    </div>
  );
}
